import * as i0 from '@angular/core';
import { Injectable, Inject, Pipe, EventEmitter, Component, Input, Output, ViewChild, ChangeDetectionStrategy, NgModule } from '@angular/core';
import * as i2 from '@engineering11/web-api-error';
import { e11Error, LOGGER_TOKEN, toE11Error } from '@engineering11/web-api-error';
import * as i1 from '@engineering11/web-utilities';
import { formatBytes, pick, isNotNil, uuid, omit, stripUndefined } from '@engineering11/utility';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i4 from '@engineering11/ui-lib/e11-button';
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button';
import * as i5 from '@engineering11/ui-lib/e11-loader-dots';
import { E11LoaderDotsModule } from '@engineering11/ui-lib/e11-loader-dots';
import * as i6 from '@engineering11/ui-lib/e11-tooltip';
import { E11TooltipModule } from '@engineering11/ui-lib/e11-tooltip';
import * as i6$1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i3$1 from '@engineering11/ui-lib/e11-file-viewer';
import { E11FileViewerModule } from '@engineering11/ui-lib/e11-file-viewer';
import * as i4$1 from '@engineering11/ui-lib/e11-directives';
import * as i5$1 from '@engineering11/ui-lib/e11-confirmation';
import { E11ConfirmationModule } from '@engineering11/ui-lib/e11-confirmation';
import { firstValueFrom, tap, filter as filter$1, switchMap, from, mergeMap, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter } from '@ngrx/entity';
import { tapResponse } from '@ngrx/operators';
import { DateService } from '@engineering11/date-time';
import jwt_decode from 'jwt-decode';
import { filter, map } from 'rxjs/operators';
import { RestApiClient } from '@engineering11/web-api-rest';
import * as i1$1 from '@engineering11/web-api-firebase';
import * as i3$2 from '@engineering11/auth-web';
import * as i3$3 from '@ngrx/component';
import { LetDirective } from '@ngrx/component';
import { uniq } from 'lodash';
import { E11DividerModule } from '@engineering11/ui-lib/e11-divider';
import { E11EmptyStateModule } from '@engineering11/ui-lib/e11-empty-state';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/svg', 'image/png', 'image/gif', 'image/heic', 'image/heif'];
const ALLOWED_VIDEO_TYPES = [
    'video/mp4',
    'video/mpeg',
    'video/ogg',
    'video/webm',
    'video/x-msvideo',
    'video/quicktime',
    'video/3gpp',
    'video/x-m4v',
];
const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/3gpp'];
const ALLOWED_DOCUMENT_TYPES = [
    'text/plain',
    'text/markdown',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/rtf',
    'application/pdf',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/epub+zip',
    'application/octet-stream',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const ALLOWED_FILE_TYPES = [
    ...ALLOWED_IMAGE_TYPES,
    ...ALLOWED_DOCUMENT_TYPES,
    ...ALLOWED_VIDEO_TYPES,
    ...ALLOWED_AUDIO_TYPES,
];

var COLLECTIONS;
(function (COLLECTIONS) {
    COLLECTIONS["FILE"] = "files/";
})(COLLECTIONS || (COLLECTIONS = {}));
var CONSTANTS;
(function (CONSTANTS) {
    CONSTANTS["STORAGE_ROOT"] = "content/";
    CONSTANTS["PUBLIC_STORAGE_ROOT"] = "assets/";
})(CONSTANTS || (CONSTANTS = {}));
const FILES_ERRORS = Object.freeze({
    FILE_TOO_LARGE: 'files/file-too-large',
    FILE_TOO_SMALL: 'files/file-too-small',
    INVALID_FILE_NAME: 'files/invalid-file-name',
    FILE_INVALID_TYPE: 'files/invalid-type',
});
const DEFAULT_FILE_VALIDATION = {
    /** 500MB */
    maxSize: 500 * 1000 * 1000, // MUST ensure this lines up with back end and storage rules
    /** 10B */
    minSize: 10,
    types: [...ALLOWED_FILE_TYPES],
};

class FileValidationService {
    constructor(logger) {
        this.logger = logger;
    }
    validate(file, validation) {
        if (validation.maxSize)
            this.validateSize(file, validation.maxSize);
        if (validation.minSize)
            this.validateMinSize(file, validation.minSize);
        if (validation.types)
            this.validateType(file, validation.types);
        this.validateName(file);
    }
    validateSize(file, bytes, errorOverrides) {
        const { size } = file;
        if (size > bytes) {
            const errorData = {
                type: FILES_ERRORS.FILE_TOO_LARGE,
                message: `File of size ${file.size} B is too large, must be ${bytes} B or less`,
                title: `Your file is ${formatBytes(file.size)} which exceeds our limit of ${formatBytes(bytes)}`,
                level: 'none', // Do not send to rollbar by default - user error
                ...errorOverrides,
            };
            throw e11Error(errorData);
        }
    }
    validateMinSize(file, bytes, errorOverrides) {
        const { size } = file;
        if (size < bytes) {
            const errorData = {
                type: FILES_ERRORS.FILE_TOO_SMALL,
                message: `File of size ${file.size} B is too small, must be ${bytes} B or more`,
                title: `Your file is ${formatBytes(file.size)} which falls below our minimum of ${formatBytes(bytes)}`,
                level: 'none', // Do not send to rollbar by default - user error
                ...errorOverrides,
            };
            throw e11Error(errorData);
        }
    }
    /**
     * Validates the MIME type of the file
     * @param file
     * @param types valid MIME types
     * @param errorOverrides
     */
    validateType(file, types, errorOverrides) {
        const { type } = file;
        const matchesSomeType = types.some((validType) => this.matchesType(validType, type));
        if (!matchesSomeType) {
            const errorData = {
                type: FILES_ERRORS.FILE_INVALID_TYPE,
                message: `File type ${type} is not one of the accepted types: ${types}`,
                title: types.length <= 5 ? `File must be of type: ${types.join(', ')}` : 'File type not accepted. Please try again',
                level: 'warning',
                ...errorOverrides,
            };
            throw e11Error(errorData);
        }
    }
    validateName(file) {
        if (file.name === 'undefined') {
            const errorData = {
                type: FILES_ERRORS.INVALID_FILE_NAME,
                message: `The filename ${file.name} is not a valid filename`,
                title: `The filename ${file.name} is not a valid filename`,
            };
            throw e11Error(errorData);
        }
    }
    matchesType(matchType, fileType) {
        const matchRegex = new RegExp(matchType.replace('*', '.*'));
        const matches = matchRegex.test(fileType);
        return matches;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileValidationService, deps: [{ token: LOGGER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileValidationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileValidationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }] });

const FILE_TYPE_DISPLAY = {
    'image/jpeg': '.jpeg, .jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/heic': '.heic',
    'image/heif': '.heif',
    'video/mp4': '.mp4',
    'video/mpeg': '.mpeg',
    'video/ogg': '.ogv',
    'video/webm': '.webm',
    'video/x-msvideo': '.avi',
    'video/quicktime': '.mov',
    'video/3gpp': '.3gp',
    'video/x-m4v': '.m4v',
    'audio/mpeg': '.mp3',
    'audio/wav': '.wav',
    'audio/3gpp': '.3gp',
    'text/plain': '.txt',
    'text/markdown': '.md',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/rtf': '.rtf',
    'application/pdf': '.pdf',
    'application/vnd.ms-powerpoint': '.ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    'application/epub+zip': '.epub',
    'application/octet-stream': '.bin',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
};

class FileTypeDisplayPipe {
    transform(fileTypes) {
        if (!fileTypes)
            return '';
        return fileTypes.map((fileType) => ' ' + FILE_TYPE_DISPLAY[fileType]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileTypeDisplayPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: FileTypeDisplayPipe, name: "fileTypeDisplay" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileTypeDisplayPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fileTypeDisplay',
                }]
        }] });

/**
 * A generic component designed to help unify & simplify the UX of uploading a file
 * Does not upload - just emits the file back out if valid
 */
class FileDropComponent {
    constructor(viewportService, logger, fileValidationService) {
        this.viewportService = viewportService;
        this.logger = logger;
        this.fileValidationService = fileValidationService;
        this.dropInstruction = 'Drag and drop your file here';
        this.loading = false; // Pass in a loader and this displays loading state
        this.validate = true; // set to false to suppress validation and conduct your own
        this.newFile = new EventEmitter();
        this.viewport = this.viewportService.getCurrentSize();
        this.smallerSizes = ['xs', 'sm'];
        this.isMobile = this.smallerSizes.includes(this.viewport);
    }
    ngOnInit() { }
    // Browse and pick
    filePicked(event) {
        this.validateAndEmit(event.target.files);
    }
    onDragOver(event) {
        event.preventDefault();
    }
    onDropSuccess(event) {
        event.preventDefault();
        this.validateAndEmit(event.dataTransfer.files);
    }
    validateAndEmit(files) {
        this.logger.log('FileDropComponent files', files);
        if (!files?.length)
            return;
        const [file] = files;
        if (this.validate)
            this.validateFile(file);
        this.logger.log('FileDropComponent emitting validated file', file);
        this.newFile.emit(file);
        this.fileUpload.nativeElement.value = ''; // Needed so that uploading the same file after removing it does not bug out
    }
    validateFile(file) {
        if (this.size)
            this.fileValidationService.validateSize(file, this.size);
        if (this.allowedFileTypes?.length)
            this.fileValidationService.validateType(file, this.allowedFileTypes);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileDropComponent, deps: [{ token: i1.ViewportService }, { token: LOGGER_TOKEN }, { token: FileValidationService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: FileDropComponent, selector: "sdk-file-drop", inputs: { dropInstruction: "dropInstruction", fileTypeErrorOverride: "fileTypeErrorOverride", allowedFileTypes: "allowedFileTypes", size: "size", loading: "loading", validate: "validate" }, outputs: { newFile: "newFile" }, viewQueries: [{ propertyName: "fileUpload", first: true, predicate: ["fileUpload"], descendants: true }], ngImport: i0, template: "<div\n  class=\"upload-container e11-w-full e11-border e11-border-dashed e11-border-skin-grey e11-items-center e11-text-center e11-rounded e11-py-4 hover:e11-cursor-pointer\"\n  [ngClass]=\"{ 'hover:e11-bg-skin-primary-accent/10': !loading, 'hover:e11-bg-skin-grey/10': loading }\"\n  [class.processing]=\"loading\"\n  [title]=\"loading ? 'File Uploader currently uploading' : 'File Uploader'\"\n  [ariaLabel]=\"loading ? 'File Uploader currently uploading' : 'File Uploader'\"\n  role=\"button\"\n  (dragover)=\"onDragOver($event)\"\n  (drop)=\"onDropSuccess($event)\"\n>\n  <div>\n    <e11-loader-dots\n      *ngIf=\"loading\"\n      [size]=\"'md'\"\n      [color]=\"'e11-bg-skin-primary-accent'\"\n      label=\"{{ '...uploading...' | translate }}\"\n    ></e11-loader-dots>\n  </div>\n\n  <div *ngIf=\"!loading\" class=\"e11-flex e11-flex-col\" (click)=\"fileUpload.click()\">\n    <div class=\"e11-w-full e11-text-center e11-mb-2\">\n      <span class=\"material-icons-outlined e11-text-skin-primary-accent md-36\"> file_upload </span>\n    </div>\n\n    <div class=\"e11-mb-2\">\n      <e11-button [size]=\"'sm'\" [value]=\"'Browse files' | translate\" [color]=\"'primary-accent'\" [tabindex]=\"-1\"> </e11-button>\n    </div>\n\n    <div class=\"e11-inline-flex e11-w-full e11-text-sm e11-text-skin-primary-accent e11-text-center e11-justify-center\">\n      <div class=\"e11-text-sm e11-text-skin-primary-accent e11-text-center e11-mb-2\">\n        {{ dropInstruction | translate }}\n      </div>\n      <e11-tooltip [color]=\"'primary-accent'\" tooltipText=\"{{ 'Supported File Types:' | translate }}{{ allowedFileTypes | fileTypeDisplay }}\">\n        <span class=\"material-icons-outlined md-18 e11-ml-1\"> info </span>\n      </e11-tooltip>\n    </div>\n\n    <input type=\"file\" class=\"e11-invisible e11-w-full e11-h-0\" [accept]=\"allowedFileTypes?.join(',')\" (change)=\"filePicked($event)\" #fileUpload />\n  </div>\n</div>\n", styles: [".upload-container:focus-within{@apply e11-bg-skin-primary-accent/10;}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i5.E11LoaderDotsComponent, selector: "e11-loader-dots", inputs: ["color", "size", "fontSize", "fontColor", "label", "buttonLoader", "fade", "fullScreen", "active", "hasBackdrop", "backdropStyle"] }, { kind: "component", type: i6.E11TooltipComponent, selector: "e11-tooltip", inputs: ["tooltipText", "tooltipClassOverrides", "hideTooltip", "color", "tooltipContent", "position", "interactive", "maxWidth", "delay"], outputs: ["tooltipClick"] }, { kind: "pipe", type: i6$1.TranslatePipe, name: "translate" }, { kind: "pipe", type: FileTypeDisplayPipe, name: "fileTypeDisplay" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileDropComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-file-drop', template: "<div\n  class=\"upload-container e11-w-full e11-border e11-border-dashed e11-border-skin-grey e11-items-center e11-text-center e11-rounded e11-py-4 hover:e11-cursor-pointer\"\n  [ngClass]=\"{ 'hover:e11-bg-skin-primary-accent/10': !loading, 'hover:e11-bg-skin-grey/10': loading }\"\n  [class.processing]=\"loading\"\n  [title]=\"loading ? 'File Uploader currently uploading' : 'File Uploader'\"\n  [ariaLabel]=\"loading ? 'File Uploader currently uploading' : 'File Uploader'\"\n  role=\"button\"\n  (dragover)=\"onDragOver($event)\"\n  (drop)=\"onDropSuccess($event)\"\n>\n  <div>\n    <e11-loader-dots\n      *ngIf=\"loading\"\n      [size]=\"'md'\"\n      [color]=\"'e11-bg-skin-primary-accent'\"\n      label=\"{{ '...uploading...' | translate }}\"\n    ></e11-loader-dots>\n  </div>\n\n  <div *ngIf=\"!loading\" class=\"e11-flex e11-flex-col\" (click)=\"fileUpload.click()\">\n    <div class=\"e11-w-full e11-text-center e11-mb-2\">\n      <span class=\"material-icons-outlined e11-text-skin-primary-accent md-36\"> file_upload </span>\n    </div>\n\n    <div class=\"e11-mb-2\">\n      <e11-button [size]=\"'sm'\" [value]=\"'Browse files' | translate\" [color]=\"'primary-accent'\" [tabindex]=\"-1\"> </e11-button>\n    </div>\n\n    <div class=\"e11-inline-flex e11-w-full e11-text-sm e11-text-skin-primary-accent e11-text-center e11-justify-center\">\n      <div class=\"e11-text-sm e11-text-skin-primary-accent e11-text-center e11-mb-2\">\n        {{ dropInstruction | translate }}\n      </div>\n      <e11-tooltip [color]=\"'primary-accent'\" tooltipText=\"{{ 'Supported File Types:' | translate }}{{ allowedFileTypes | fileTypeDisplay }}\">\n        <span class=\"material-icons-outlined md-18 e11-ml-1\"> info </span>\n      </e11-tooltip>\n    </div>\n\n    <input type=\"file\" class=\"e11-invisible e11-w-full e11-h-0\" [accept]=\"allowedFileTypes?.join(',')\" (change)=\"filePicked($event)\" #fileUpload />\n  </div>\n</div>\n", styles: [".upload-container:focus-within{@apply e11-bg-skin-primary-accent/10;}\n"] }]
        }], ctorParameters: () => [{ type: i1.ViewportService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: FileValidationService }], propDecorators: { dropInstruction: [{
                type: Input
            }], fileTypeErrorOverride: [{
                type: Input
            }], allowedFileTypes: [{
                type: Input
            }], size: [{
                type: Input
            }], loading: [{
                type: Input
            }], validate: [{
                type: Input
            }], newFile: [{
                type: Output
            }], fileUpload: [{
                type: ViewChild,
                args: ['fileUpload']
            }] } });

class FileTypeIsImagePipe {
    transform(mimeType) {
        return /^image\//.test(mimeType);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileTypeIsImagePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: FileTypeIsImagePipe, name: "fileTypeIsImage" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileTypeIsImagePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fileTypeIsImage',
                }]
        }] });

class FileLineItemComponent {
    constructor(errorHandler) {
        this.errorHandler = errorHandler;
        this.loading = false;
        this.fileDeleted = new EventEmitter();
        this.showFile = false;
    }
    openConfirmation(event) {
        this.confirmation.open();
        event.stopPropagation();
    }
    confirmationAnswer(res) {
        if (res)
            this.fileDeleted.emit(this.file);
    }
    openFile(item) {
        if (!item?.signedURL || !item.fileName) {
            this.errorHandler.handleError(e11Error(errorViewingFile(item)), { alertUser: true });
        }
        this.showFile = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileLineItemComponent, deps: [{ token: i2.E11ErrorHandlerService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: FileLineItemComponent, selector: "sdk-file-line-item", inputs: { file: "file", loading: "loading" }, outputs: { fileDeleted: "fileDeleted" }, viewQueries: [{ propertyName: "confirmation", first: true, predicate: ["confirmation"], descendants: true }], ngImport: i0, template: `
    <div class="e11-flex e11-items-center">
      <e11-file-viewer
        *ngIf="showFile && file?.fileName && file?.signedURL"
        (close)="showFile = false"
        [name]="file?.fileName!"
        [url]="file?.signedURL!"
      ></e11-file-viewer>
    </div>

    <div
      (click)="!loading && openFile(file)"
      (keydown.enter)="!loading && openFile(file)"
      class="e11-flex e11-w-full e11-p-2 e11-border e11-border-skin-app-borders e11-rounded-md"
      title="View this file"
      role="button"
      aria-label="View this file"
      [tabIndex]="0"
      [ngClass]="{
        'line-item e11-cursor-pointer focus:e11-bg-skin-primary-accent/10 focus:e11-border-skin-primary-accent focus:e11-shadow-sm': file
      }"
    >
      <div class="e11-flex e11-grow e11-items-center e11-justify-start e11-line-clamp-1 e11-whitespace-nowrap">
        <span *ngIf="!loading" class="e11-skeleton-empty e11-pr-2 material-icons-outlined">{{
          (file?.mimeType! | fileTypeIsImage) ? 'image' : 'description'
        }}</span>
        <p *ngIf="!loading" class="e11-mb-0 e11-text-sm e11-text-skin-primary-accent e11-skeleton-empty">
          {{ file?.fileName }}
        </p>
        <div *ngIf="loading" class="e11-ml-6 e11-skeleton-empty"></div>
      </div>
      <div *ngIf="!loading" class="e11-flex e11-justify-end e11-items-center">
        <span
          *ngIf="file"
          tabindex="0"
          title="Delete this file"
          role="button"
          aria-label="Delete this file"
          class="delete-button material-icons-outlined e11-text-skin-secondary e11-p-1 md-18 hover:e11-bg-skin-secondary/20 hover:e11-rounded-full hover:e11-cursor-pointer focus:e11-bg-skin-secondary/20 focus:e11-rounded-full"
          (click)="!loading && openConfirmation($event)"
          (keydown.enter)="!loading && openConfirmation($event)"
          >delete</span
        >
      </div>
    </div>

    <e11-confirmation
      #confirmation
      confirmTitle="{{ 'Delete this file' | translate }}"
      confirmText="{{ 'confirm' | translate }}"
      [closeOnEscape]="false"
      cancelText="{{ 'cancel' | translate }}"
      [backdropStyle]="'dark'"
      (answer)="confirmationAnswer($event)"
    >
      {{ 'Are you sure you want to remove this file? You can always add it back if you need to.' | translate }}
    </e11-confirmation>
  `, isInline: true, styles: [".line-item:hover:not(:has(.delete-button:hover)){@apply e11-bg-skin-primary-accent/10 e11-border-skin-primary-accent e11-shadow-sm;}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i3$1.E11FileViewerComponent, selector: "e11-file-viewer", inputs: ["url", "name"], outputs: ["close"] }, { kind: "directive", type: i4$1.EscapeKeyDirective, selector: "[closeOnEscape]", outputs: ["closeOnEscape"] }, { kind: "component", type: i5$1.E11ConfirmationComponent, selector: "e11-confirmation", inputs: ["confirmTitle", "confirmText", "cancelText", "confirmClassOverrides", "cancelClassOverrides", "isMobile", "processing", "processingText", "closeOnEscape", "closeOnClickOutside", "hasBackdrop", "backdropStyle", "confirmValueWithInputBox", "valueToConfirm"], outputs: ["answer"] }, { kind: "pipe", type: i6$1.TranslatePipe, name: "translate" }, { kind: "pipe", type: FileTypeIsImagePipe, name: "fileTypeIsImage" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileLineItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-file-line-item', template: `
    <div class="e11-flex e11-items-center">
      <e11-file-viewer
        *ngIf="showFile && file?.fileName && file?.signedURL"
        (close)="showFile = false"
        [name]="file?.fileName!"
        [url]="file?.signedURL!"
      ></e11-file-viewer>
    </div>

    <div
      (click)="!loading && openFile(file)"
      (keydown.enter)="!loading && openFile(file)"
      class="e11-flex e11-w-full e11-p-2 e11-border e11-border-skin-app-borders e11-rounded-md"
      title="View this file"
      role="button"
      aria-label="View this file"
      [tabIndex]="0"
      [ngClass]="{
        'line-item e11-cursor-pointer focus:e11-bg-skin-primary-accent/10 focus:e11-border-skin-primary-accent focus:e11-shadow-sm': file
      }"
    >
      <div class="e11-flex e11-grow e11-items-center e11-justify-start e11-line-clamp-1 e11-whitespace-nowrap">
        <span *ngIf="!loading" class="e11-skeleton-empty e11-pr-2 material-icons-outlined">{{
          (file?.mimeType! | fileTypeIsImage) ? 'image' : 'description'
        }}</span>
        <p *ngIf="!loading" class="e11-mb-0 e11-text-sm e11-text-skin-primary-accent e11-skeleton-empty">
          {{ file?.fileName }}
        </p>
        <div *ngIf="loading" class="e11-ml-6 e11-skeleton-empty"></div>
      </div>
      <div *ngIf="!loading" class="e11-flex e11-justify-end e11-items-center">
        <span
          *ngIf="file"
          tabindex="0"
          title="Delete this file"
          role="button"
          aria-label="Delete this file"
          class="delete-button material-icons-outlined e11-text-skin-secondary e11-p-1 md-18 hover:e11-bg-skin-secondary/20 hover:e11-rounded-full hover:e11-cursor-pointer focus:e11-bg-skin-secondary/20 focus:e11-rounded-full"
          (click)="!loading && openConfirmation($event)"
          (keydown.enter)="!loading && openConfirmation($event)"
          >delete</span
        >
      </div>
    </div>

    <e11-confirmation
      #confirmation
      confirmTitle="{{ 'Delete this file' | translate }}"
      confirmText="{{ 'confirm' | translate }}"
      [closeOnEscape]="false"
      cancelText="{{ 'cancel' | translate }}"
      [backdropStyle]="'dark'"
      (answer)="confirmationAnswer($event)"
    >
      {{ 'Are you sure you want to remove this file? You can always add it back if you need to.' | translate }}
    </e11-confirmation>
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [".line-item:hover:not(:has(.delete-button:hover)){@apply e11-bg-skin-primary-accent/10 e11-border-skin-primary-accent e11-shadow-sm;}\n"] }]
        }], ctorParameters: () => [{ type: i2.E11ErrorHandlerService }], propDecorators: { confirmation: [{
                type: ViewChild,
                args: ['confirmation']
            }], file: [{
                type: Input
            }], loading: [{
                type: Input
            }], fileDeleted: [{
                type: Output
            }] } });
const errorViewingFile = (file) => ({
    title: 'There was an error viewing this file',
    type: 'files/file-view-error',
    additionalData: file,
});

class FileListComponent {
    constructor() {
        this.columns = 2;
        this.addingFile = false;
        this.fileDeleted = new EventEmitter();
    }
    set fileList(fileList) {
        this._fileList = fileList;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: FileListComponent, selector: "sdk-file-list", inputs: { fileList: "fileList", columns: "columns", addingFile: "addingFile" }, outputs: { fileDeleted: "fileDeleted" }, ngImport: i0, template: `
    <div
      *ngIf="!addingFile && (!_fileList || _fileList.length === 0)"
      class="e11-flex e11-justify-center e11-h-full e11-text-center e11-items-center"
    >
      <span class="e11-text-skin-base">{{ 'No files uploaded' | translate }}</span>
    </div>
    <div class="e11-grid e11-gap-4 sm:e11-grid-cols-{{ columns }} e11-grid-cols-1">
      <ng-container *ngFor="let file of _fileList">
        <sdk-file-line-item [file]="file" (fileDeleted)="fileDeleted.emit($event)"></sdk-file-line-item>
      </ng-container>
      <div *ngIf="addingFile">
        <sdk-file-line-item [loading]="true"></sdk-file-line-item>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: FileLineItemComponent, selector: "sdk-file-line-item", inputs: ["file", "loading"], outputs: ["fileDeleted"] }, { kind: "pipe", type: i6$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileListComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-file-list',
                    template: `
    <div
      *ngIf="!addingFile && (!_fileList || _fileList.length === 0)"
      class="e11-flex e11-justify-center e11-h-full e11-text-center e11-items-center"
    >
      <span class="e11-text-skin-base">{{ 'No files uploaded' | translate }}</span>
    </div>
    <div class="e11-grid e11-gap-4 sm:e11-grid-cols-{{ columns }} e11-grid-cols-1">
      <ng-container *ngFor="let file of _fileList">
        <sdk-file-line-item [file]="file" (fileDeleted)="fileDeleted.emit($event)"></sdk-file-line-item>
      </ng-container>
      <div *ngIf="addingFile">
        <sdk-file-line-item [loading]="true"></sdk-file-line-item>
      </div>
    </div>
  `,
                }]
        }], propDecorators: { fileList: [{
                type: Input
            }], columns: [{
                type: Input
            }], addingFile: [{
                type: Input
            }], fileDeleted: [{
                type: Output
            }] } });

class CoreUploadService {
    constructor(configProvider, fileValidationService) {
        this.configProvider = configProvider;
        this.fileValidationService = fileValidationService;
        this.validation = { ...DEFAULT_FILE_VALIDATION, ...this.configProvider.config.validationConfig };
        this.restClient = this.createRestClient();
    }
    createRestClient() {
        if (!this.configProvider.config.filesBaseURL) {
            throw new Error('Missing configuration, filesBaseURL is undefined. Configure Files module with an IFilesConfig model');
        }
        return new RestApiClient({
            baseUrl: this.configProvider.config.filesBaseURL,
            token: this.configProvider.config.token$,
            throwNon200s: true,
        });
    }
    validateFileUpload(file, validation) {
        this.fileValidationService.validate(file, validation);
    }
    constructFileUrl(pathname) {
        return encodeURI(`https://${this.configProvider.config.CNAME}/${pathname}`);
    }
}

class BaseFilesConfigProvider {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseFilesConfigProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseFilesConfigProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseFilesConfigProvider, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
/**
 * @deprecated use BaseFilesConfigProvider - less name clashing
 * TODO: On breaking change, delete
 */
class FilesConfigProvider extends BaseFilesConfigProvider {
}

class FilesStorageRepository {
    constructor(storageService) {
        this.storageService = storageService;
    }
    buildPath(model) {
        return this.buildPathCore(this.toBuildPathCodeArgs(model));
    }
    buildPathToFileRoot(model) {
        const { fileName, ...arg } = this.toBuildPathCodeArgs(model);
        return this.buildPathCore(arg);
    }
    buildPathToContentItemRoot(customerKey, groupingId, fileId, isPublic = false) {
        return this.buildPathCore({ customerKey, groupingId, fileId, isPublic });
    }
    buildPathFullPath(customerKey, groupingId, fileId, fileName = '', isPublic = false) {
        return this.buildPathCore({ customerKey, groupingId, fileId, fileName, isPublic });
    }
    buildPathCore(args) {
        const { customerKey, fileId, groupingId, fileName = '', isPublic = false } = args;
        const storageRoot = isPublic ? CONSTANTS.PUBLIC_STORAGE_ROOT : CONSTANTS.STORAGE_ROOT;
        return `${storageRoot}${customerKey}/${groupingId}/${fileId}/${fileName}`;
    }
    toBuildPathCodeArgs(model) {
        return {
            ...pick(model, 'customerKey', 'groupingId', 'fileName'),
            fileId: model.id,
            isPublic: model.public,
        };
    }
    async uploadWithoutMetadata(file, fileReceipt, options) {
        const { uploadSettings } = options || {};
        return await this.storageService.upload(file, this.buildPath(fileReceipt), uploadSettings);
    }
    async upload(file, fileReceipt, options) {
        const { uploadSettings } = options || {};
        return await this.storageService.uploadWithMetadata(file, this.buildPath(fileReceipt), fileReceipt, uploadSettings);
    }
    async update(file, fileReceipt, options) {
        const { uploadSettings } = options || {};
        return await this.storageService.uploadWithMetadata(file, this.buildPath(fileReceipt), fileReceipt, uploadSettings);
    }
    delete() {
        throw new Error('Operation not supported');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesStorageRepository, deps: [{ token: i1$1.StorageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesStorageRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesStorageRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$1.StorageService }] });

const PUBLIC_FILE_ROOT = 'assets/';
/**
 * @deprecated
 */
class PublicFileRepository {
    buildPath(fileName, folder, key) {
        return `${PUBLIC_FILE_ROOT}${key}/${folder}/${fileName}`;
    }
    constructor(storageService) {
        this.storageService = storageService;
    }
    /**
     * upload a public file
     *
     * @param file
     * @param folder
     * @param key
     * @returns
     */
    async upload(file, CNAME, fileReceipt) {
        const path = this.buildPath(file.name, fileReceipt.groupingId, fileReceipt.customerKey);
        const result = await this.storageService.uploadWithMetadata(file, path, fileReceipt);
        return {
            publicDownloadUrl: encodeURI(`https://${CNAME}/${result.fullPath}`),
            internalDownloadUrl: result.downloadURL,
            path: path,
        };
    }
    async delete(downLoadUrl) {
        const url = new URL(downLoadUrl);
        await this.storageService.delete(url.pathname);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PublicFileRepository, deps: [{ token: i1$1.StorageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PublicFileRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PublicFileRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$1.StorageService }] });

class FilesService extends CoreUploadService {
    constructor(configProvider, storage, authService, publicFileRepository, fileValidationService, errorHandler) {
        super(configProvider, fileValidationService);
        this.configProvider = configProvider;
        this.storage = storage;
        this.authService = authService;
        this.publicFileRepository = publicFileRepository;
        this.fileValidationService = fileValidationService;
        this.errorHandler = errorHandler;
        this.customerKey$ = this.configProvider.config.customerKey$.pipe(filter(isNotNil)); // If we use it, we need to wait for a nonNil value
    }
    /** handles the error and alerts the user */
    uploadFile(request) {
        return this.storageUpload(request).catch((error) => {
            this.errorHandler.handleError(error, { alertUser: true });
            throw error;
        });
    }
    /**
     * Files uploaded this way will have the same groupingId
     * TODO: graceful error handling in the event one file upload fails
     */
    async uploadFiles(request) {
        const defaultGroupingId = uuid();
        const uploadPromises = request.files.map((file) => this.uploadFile({ ...omit(request, 'files'), file, groupingId: request.groupingId ?? defaultGroupingId }));
        return Promise.all(uploadPromises);
    }
    async delete(secretToken) {
        return firstValueFrom(this.restClient.delete(`files/${secretToken}/delete`));
    }
    /** Helper method to delete many files at once by passing an array of tokens */
    async deleteMany(secretTokens) {
        const promises = secretTokens.map((token) => this.delete(token));
        return Promise.all(promises);
    }
    async deleteFromUrl(downloadUrl, config = { throwOnError: true }) {
        return firstValueFrom(this.restClient.delete(`files/url-delete?downloadUrl=${downloadUrl}`)).catch((error) => {
            if (config.throwOnError)
                throw error;
            this.errorHandler.handleError(error, { level: 'error' });
        });
    }
    async storageUpload(request) {
        const { file, storageOptions } = request;
        const { customerKey, groupingId, makePublic, validation } = await this.getUploadRequestDefaults(request);
        this.validateFileUpload(file, validation);
        const fileName = file.name.replace(/ /g, '_');
        const payload = {
            customerKey,
            groupingId,
            makePublic,
            fileName,
            mimeType: file.type,
            size: file.size,
            deletionDelayDays: request.deletionDelayDays,
        };
        const receiptResponse = await firstValueFrom(this.restClient.post('files/file-receipt', payload));
        const fileReceipt = receiptResponse.data;
        await this.storage.upload(file, fileReceipt, storageOptions);
        const query = new URLSearchParams({
            saveAsTemp: `${!!request.saveAsTemp}`,
        });
        const finalFileReceipt = await firstValueFrom(this.restClient.put(`files/finalize-upload?${query.toString()}`, fileReceipt));
        return finalFileReceipt.data;
    }
    async getUploadRequestDefaults(request) {
        const defaultValues = { groupingId: uuid(), makePublic: false, saveAsTemp: false, storageOptions: {} };
        const requestWithoutUndefined = stripUndefined(request); // if user passes undefined for groupingId, we want to use the generated one
        const validation = { ...this.validation, ...request.validation };
        // If customerKey is passed in, use it. Otherwise, get customerKey from config
        if (request.customerKey)
            return { ...defaultValues, customerKey: request.customerKey, ...requestWithoutUndefined, validation };
        const customerKey = await firstValueFrom(this.customerKey$);
        return { ...defaultValues, customerKey, ...requestWithoutUndefined, validation };
    }
    async getMetaData(secretToken) {
        const response = await this.restClient.get(`files/${secretToken}/metadata`).toPromise();
        const metadata = DateService.convertAllDateFields(response.data);
        return metadata;
    }
    async signedURLs(files) {
        if (!files || files.length < 1)
            return [];
        const result = await this.restClient.post('signed-url', { files }).toPromise();
        return files.map((f) => ({ ...f, signedURL: result?.data[f.id] }));
    }
    async purgeSignedUrlCache() {
        const result = await this.restClient.post('signed-url/purge-cache', {}).toPromise();
        return result?.data;
    }
    async getVideoFallbackURL(file) {
        if (file.public) {
            const { CNAME } = this.configProvider.config;
            return `https://${CNAME}/${this.storage.buildPath(file)}`;
        }
        const result = await firstValueFrom(this.restClient.post('signed-url', { files: [file], forceStorageSigning: true }));
        return result?.data[file.id];
    }
    async copyFile(fileReceipt) {
        const response = await this.restClient.post('files/copy-file', { fileReceipt: fileReceipt }).toPromise();
        return response.data;
    }
    async generatePDF(options) {
        const urlParams = Object.keys(options)
            .map((key) => `${key}=${options[key]}`)
            .join('&');
        const response = await this.restClient.get(`generate-pdf?${urlParams}`, { version: '2' }).toPromise();
        return response.data;
    }
    async downloadPDF(options, fileName) {
        const downloadUrl = await this.generatePDF(options);
        try {
            const response = await fetch(downloadUrl.url);
            const blob = await response.blob();
            this.downloadFile(blob, fileName);
        }
        catch (err) {
            throw toE11Error(err, { title: 'Error fetching pdf file', type: 'files/pdf-download' });
        }
    }
    downloadFile(blob, filename) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        link.click();
    }
    /**
     * @deprecated --  upload() with makePublic=true is the preferred method but it is incompatible with this. If this has been used
     * already for storage files a migration would be necessary because the paths are different.
     *
     * @param file
     * @param folder
     * @returns
     */
    async uploadPublicFile(file, folder) {
        const authToken = await this.getUserOrError().getIdToken();
        const decodedToken = jwt_decode(authToken);
        const payload = {
            customerKey: decodedToken.customerKey,
            groupingId: folder,
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
        };
        const response = await this.restClient.post('files/file-receipt', payload).toPromise();
        const fileReceipt = response.data;
        const uploadResult = await this.publicFileRepository.upload(file, this.configProvider.config.CNAME, fileReceipt);
        fileReceipt.url = uploadResult.publicDownloadUrl;
        await this.restClient.put('files/make-public', fileReceipt).toPromise();
        return fileReceipt;
    }
    /**
     * @deprecated use @uploadFile
     * @param file
     * @param customerKey
     * @param groupingId
     * @param makePublic
     * @returns
     */
    async upload(file, customerKey, groupingId, makePublic = false) {
        return this.storageUpload({ file, customerKey, groupingId, makePublic }); // ! Until we can properly debug endpointUpload for larger file sizes (larger than 32MB)
    }
    /**
     * ! Warning: do not use this until we debug for larger file sizes (larger than 32MB)
     * @param file
     * @param customerKey
     * @param groupingId
     * @param makePublic
     * @returns
     */
    async endpointUpload(request) {
        const { file } = request;
        const { customerKey, groupingId, makePublic, validation } = await this.getUploadRequestDefaults(request);
        this.validateFileUpload(file, validation);
        const form = new FormData();
        form.append('file', file);
        const response = await this.restClient
            .uploadFile(`files/${groupingId}/upload?public=${makePublic}`, form)
            .pipe(map((response) => response.data))
            .toPromise();
        return response;
    }
    /**
     *
     * @deprecated use `delete` or `deleteFromUrl`
     */
    async deletePublicFile(downloadURL) {
        return this.publicFileRepository.delete(downloadURL);
    }
    getUserOrError() {
        if (!this.authService.user) {
            throw e11Error({
                type: 'users/no-user',
                title: 'No user found',
            });
        }
        return this.authService.user;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesService, deps: [{ token: BaseFilesConfigProvider }, { token: FilesStorageRepository }, { token: i3$2.AuthService }, { token: PublicFileRepository }, { token: FileValidationService }, { token: i2.E11ErrorHandlerService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: BaseFilesConfigProvider }, { type: FilesStorageRepository }, { type: i3$2.AuthService }, { type: PublicFileRepository }, { type: FileValidationService }, { type: i2.E11ErrorHandlerService }] });

const fileAdapter = createEntityAdapter({
    selectId: (file) => file.id,
});
const fileSelectors = fileAdapter.getSelectors();
const initialState = fileAdapter.getInitialState({
    uploading: false,
    signingFiles: false,
    lastFileUploadInternalChange: null,
});
class UploadFileStore extends ComponentStore {
    constructor(filesService, errorHandler, logger) {
        super(initialState);
        this.filesService = filesService;
        this.errorHandler = errorHandler;
        this.logger = logger;
        this.getState = this.select((s) => s);
        this.signedFiles$ = this.select((s) => fileSelectors.selectAll(s));
        this.uploading$ = this.select((s) => s.uploading);
        this.lastFileUploadInternalChange$ = this.select((s) => s.lastFileUploadInternalChange);
        this.signingFiles$ = this.select((s) => s.signingFiles);
        this.setFiles = this.effect((files$) => files$.pipe(tap((files) => this.setAllFiles(files)), filter$1((files) => files.length > 0), tap(() => this.onSigningFiles(true)), switchMap((files) => {
            const signedFiles = Object.values(this.get().entities)
                .filter((file) => !!file?.signedURL)
                .map((file) => file?.id);
            const unsignedFiles = files.filter((file) => !signedFiles.includes(file.id));
            this.logger.log('Signing Files: ', unsignedFiles);
            return from(this.filesService.signedURLs(unsignedFiles)).pipe(tapResponse((signedFiles) => {
                this.onFilesSigned(signedFiles);
            }, (error) => {
                this.errorHandler.handleError(error);
                this.onSigningFiles(false);
            }));
        })));
        this.uploadFile = this.effect((file$) => file$.pipe(tap(() => this.onUploading(true)), mergeMap((file) => {
            return from(this.filesService.uploadFile({ file })).pipe(tapResponse((uploadedFile) => {
                this.onFileAdded(uploadedFile);
            }, (error) => {
                this.errorHandler.handleError(error);
                this.onUploading(false);
            }));
        })));
        this.deleteFile = this.effect((file$) => file$.pipe(mergeMap(async (file) => this.onFileDeleted(file.id))));
        this.setAllFiles = this.updater((state, files) => {
            const fileEntities = state.entities;
            const filesToSet = files.map((f) => preserveSignedUrl(f, fileEntities[f.id]?.signedURL));
            return fileAdapter.setAll(filesToSet, { ...state });
        });
        this.onFileAdded = this.updater((state, file) => fileAdapter.addOne(file, { ...state, lastFileUploadInternalChange: new Date(), uploading: false }));
        this.onFileDeleted = this.updater((state, id) => fileAdapter.removeOne(id, { ...state, lastFileUploadInternalChange: new Date() }));
        this.onFilesSigned = this.updater((state, signedFiles) => fileAdapter.setMany(signedFiles, { ...state, signingFiles: false }));
        this.onUploading = this.updater((state, uploading) => ({ ...state, uploading }));
        this.onSigningFiles = this.updater((state, signingFiles) => ({ ...state, signingFiles }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UploadFileStore, deps: [{ token: FilesService }, { token: i2.E11ErrorHandlerService }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UploadFileStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UploadFileStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: FilesService }, { type: i2.E11ErrorHandlerService }, { type: i2.E11Logger }] });
function preserveSignedUrl(file, signedURL) {
    return { ...file, signedURL };
}

const FileUploadDefaultFileTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/heic',
    'image/heif',
];
class FileUploadComponent {
    set fileList(fileList) {
        this.uploadFileStore.setFiles(fileList);
    }
    constructor(uploadFileStore, logger) {
        this.uploadFileStore = uploadFileStore;
        this.logger = logger;
        this.allowedFileTypes = FileUploadDefaultFileTypes;
        this.newFiles = new EventEmitter();
        this.fileDeleted = new EventEmitter();
        this.files$ = this.uploadFileStore.signedFiles$;
        this.uploading$ = this.uploadFileStore.uploading$;
        this.destroy$ = new Subject();
    }
    async ngOnInit() {
        this.uploadFileStore.lastFileUploadInternalChange$
            .pipe(takeUntil(this.destroy$), filter$1(isNotNil), withLatestFrom(this.files$))
            .subscribe(([lastFileUploadInternalChange, newFiles]) => {
            this.logger.log(`New Files: `, { lastFileUploadInternalChange, newFiles });
            this.newFiles.emit(newFiles.map((file) => this.stripSignedUrl(file)));
        });
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    handleFileAdded(file) {
        this.uploadFileStore.uploadFile(file);
    }
    handleFileDeleted(file) {
        this.fileDeleted.emit(file);
        this.uploadFileStore.deleteFile(file);
    }
    stripSignedUrl(file) {
        const { signedURL, ...strippedFile } = file;
        return strippedFile;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileUploadComponent, deps: [{ token: UploadFileStore }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: FileUploadComponent, selector: "sdk-file-upload", inputs: { fileList: "fileList", maxFileSize: "maxFileSize", allowedFileTypes: "allowedFileTypes" }, outputs: { newFiles: "newFiles", fileDeleted: "fileDeleted" }, ngImport: i0, template: `
    <ng-container *ngrxLet="files$ as files">
      <ng-container *ngrxLet="uploading$ as uploading">
        <div class="e11-grid e11-grid-cols-1 sm:e11-grid-cols-3 sm:e11-gap-4">
          <div class="e11-mb-4 sm:e11-mb-0">
            <sdk-file-drop
              [allowedFileTypes]="allowedFileTypes"
              [loading]="uploading"
              [size]="maxFileSize"
              (newFile)="handleFileAdded($event)"
            ></sdk-file-drop>
          </div>
          <div class="e11-col-span-2">
            <sdk-file-list [fileList]="files" [addingFile]="uploading" (fileDeleted)="handleFileDeleted($event)"></sdk-file-list>
          </div>
        </div>
      </ng-container>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "directive", type: i3$3.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "component", type: FileDropComponent, selector: "sdk-file-drop", inputs: ["dropInstruction", "fileTypeErrorOverride", "allowedFileTypes", "size", "loading", "validate"], outputs: ["newFile"] }, { kind: "component", type: FileListComponent, selector: "sdk-file-list", inputs: ["fileList", "columns", "addingFile"], outputs: ["fileDeleted"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FileUploadComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-file-upload',
                    template: `
    <ng-container *ngrxLet="files$ as files">
      <ng-container *ngrxLet="uploading$ as uploading">
        <div class="e11-grid e11-grid-cols-1 sm:e11-grid-cols-3 sm:e11-gap-4">
          <div class="e11-mb-4 sm:e11-mb-0">
            <sdk-file-drop
              [allowedFileTypes]="allowedFileTypes"
              [loading]="uploading"
              [size]="maxFileSize"
              (newFile)="handleFileAdded($event)"
            ></sdk-file-drop>
          </div>
          <div class="e11-col-span-2">
            <sdk-file-list [fileList]="files" [addingFile]="uploading" (fileDeleted)="handleFileDeleted($event)"></sdk-file-list>
          </div>
        </div>
      </ng-container>
    </ng-container>
  `,
                }]
        }], ctorParameters: () => [{ type: UploadFileStore }, { type: i2.E11Logger }], propDecorators: { fileList: [{
                type: Input
            }], maxFileSize: [{
                type: Input
            }], allowedFileTypes: [{
                type: Input
            }], newFiles: [{
                type: Output
            }], fileDeleted: [{
                type: Output
            }] } });

/**
 * TODO: Use e11-avatar for preview image. Fix deleteFromUrl bug where spaces in filename are not deletable (backend bug)
 */
var FILE_MODE;
(function (FILE_MODE) {
    FILE_MODE["URL"] = "url";
    FILE_MODE["RECEIPT"] = "receipt";
})(FILE_MODE || (FILE_MODE = {}));
class PhotoEditComponent {
    constructor(filesService, logger) {
        this.filesService = filesService;
        this.logger = logger;
        this.mode = FILE_MODE.URL;
        this.makePublic = true;
        this.newPhotoUrl = new EventEmitter();
        this.newPhotoReceipt = new EventEmitter();
        this.removePhoto = new EventEmitter();
        this.loading = false;
    }
    ngOnInit() {
        if (this.mode === FILE_MODE.RECEIPT) {
            this.currentPhotoUrl = this.currentPhotoReceipt?.url;
        }
    }
    async addNewPhoto(event) {
        if (event.target.files && event.target.files?.length) {
            this.loading = true;
            const file = event.target.files[0];
            await this.uploadNewFile(file);
            await this.deleteCurrentPhotoFile(); // file cleanup - no need to emit since new photo will overwrite
            this.loading = false;
        }
        this.photoInput.nativeElement.value = ''; // Needed so that uploading the same photo after removing it does not bug out
    }
    async removeCurrentPhoto() {
        await this.deleteCurrentPhotoFile();
        this.removePhoto.emit();
    }
    async uploadNewFile(file) {
        const receipt = await this.filesService.uploadFile({ file, makePublic: this.makePublic });
        if (this.mode === FILE_MODE.URL) {
            this.newPhotoUrl.emit(receipt.url);
        }
        else {
            this.newPhotoReceipt.emit(receipt);
        }
    }
    async deleteCurrentPhotoFile() {
        this.loading = true;
        // Automated file management should delete file receipts when they are no longer needed, but it does not support tracking URLs
        if (this.mode === FILE_MODE.URL && this.currentPhotoUrl) {
            await this.filesService.deleteFromUrl(this.currentPhotoUrl);
        }
        this.currentPhotoUrl = undefined;
        this.currentPhotoReceipt = undefined;
        this.loading = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PhotoEditComponent, deps: [{ token: FilesService }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: PhotoEditComponent, selector: "sdk-photo-edit", inputs: { currentPhotoUrl: "currentPhotoUrl", currentPhotoReceipt: "currentPhotoReceipt", mode: "mode", makePublic: "makePublic" }, outputs: { newPhotoUrl: "newPhotoUrl", newPhotoReceipt: "newPhotoReceipt", removePhoto: "removePhoto" }, viewQueries: [{ propertyName: "photoInput", first: true, predicate: ["fileInput"], descendants: true }], ngImport: i0, template: "<div class=\"row\">\n  <div class=\"col-xs-12 text-center\">\n    <div class=\"avatar-upload\">\n      <div class=\"avatar-preview\" [ngClass]=\"{ loading: loading }\">\n        <!-- @Todo: Austin photo loader -->\n        <span class=\"material-icons e11-text-6xl\" *ngIf=\"!currentPhotoUrl\">person</span>\n        <div *ngIf=\"currentPhotoUrl\" id=\"imagePreview\" [style.backgroundImage]=\"'url(' + currentPhotoUrl + ')'\"></div>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div class=\"e11-w-full\">\n  <div class=\"e11-mb-4 e11-px-6 lg:e11-px-16\">\n    <input class=\"input-file-uploader\" type=\"file\" id=\"imageUpload\" accept=\".png, .jpg, .jpeg\" #fileInput (change)=\"addNewPhoto($event)\" />\n    <e11-button\n      id=\"button_upload\"\n      value=\"{{ loading ? 'Loading...' : ('Upload from computer' | translate) }}\"\n      color=\"primary-accent\"\n      [type]=\"'button'\"\n      [ghost]=\"true\"\n      [icon]=\"'file_upload'\"\n      [iconPosition]=\"'left'\"\n      (click)=\"fileInput.click()\"\n      [disabled]=\"loading\"\n      [display]=\"'block'\"\n    >\n    </e11-button>\n  </div>\n  <div class=\"e11-mb-1 e11-px-6 lg:e11-px-16\">\n    <e11-button\n      id=\"button_submit\"\n      value=\"{{ 'Remove photo' | translate }}\"\n      color=\"secondary\"\n      [type]=\"'button'\"\n      [ghost]=\"true\"\n      [icon]=\"'cancel'\"\n      [iconPosition]=\"'left'\"\n      (click)=\"removeCurrentPhoto()\"\n      [disabled]=\"!currentPhotoUrl || loading\"\n      [display]=\"'block'\"\n    >\n    </e11-button>\n  </div>\n</div>\n", styles: [".input-file-uploader{overflow:hidden;opacity:0;height:0;width:0}avatar-upload{position:relative;max-width:6.25rem;margin:.625rem auto 1.875rem}.avatar-upload .avatar-edit{position:absolute;right:19px;z-index:1;top:13px}.avatar-upload .avatar-edit .custom-label{display:inline-block;width:2.125rem;height:2.125rem;margin-bottom:0;border-radius:100%;background:#3efc78;border:1px solid transparent;cursor:pointer;font-weight:400;transition:all .2s ease-in-out}.avatar-upload .avatar-edit .upload-image{background:#3efc78}.avatar-upload .avatar-edit .remove-image{background:#e91e63}.avatar-upload .avatar-edit .custom-label:hover{opacity:1}.avatar-upload .avatar-edit .custom-label:after{position:absolute;text-align:center;margin:auto;top:.5rem;right:0;left:2px;width:1.125rem}.avatar-upload .avatar-preview{width:100px;height:100px;position:relative;border-radius:100%;overflow:hidden;margin:0 auto;margin-bottom:1.5rem;background:#e3e3e3;display:flex;flex-direction:column;justify-content:center;align-items:center}.avatar-upload .avatar-preview>div{width:100%;height:100%;border-radius:100%;background-size:cover;background-repeat:no-repeat;background-position:center}.avatar-upload .avatar-preview>.default{font-size:3.5rem;color:#acacac}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "pipe", type: i6$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PhotoEditComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-photo-edit', template: "<div class=\"row\">\n  <div class=\"col-xs-12 text-center\">\n    <div class=\"avatar-upload\">\n      <div class=\"avatar-preview\" [ngClass]=\"{ loading: loading }\">\n        <!-- @Todo: Austin photo loader -->\n        <span class=\"material-icons e11-text-6xl\" *ngIf=\"!currentPhotoUrl\">person</span>\n        <div *ngIf=\"currentPhotoUrl\" id=\"imagePreview\" [style.backgroundImage]=\"'url(' + currentPhotoUrl + ')'\"></div>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div class=\"e11-w-full\">\n  <div class=\"e11-mb-4 e11-px-6 lg:e11-px-16\">\n    <input class=\"input-file-uploader\" type=\"file\" id=\"imageUpload\" accept=\".png, .jpg, .jpeg\" #fileInput (change)=\"addNewPhoto($event)\" />\n    <e11-button\n      id=\"button_upload\"\n      value=\"{{ loading ? 'Loading...' : ('Upload from computer' | translate) }}\"\n      color=\"primary-accent\"\n      [type]=\"'button'\"\n      [ghost]=\"true\"\n      [icon]=\"'file_upload'\"\n      [iconPosition]=\"'left'\"\n      (click)=\"fileInput.click()\"\n      [disabled]=\"loading\"\n      [display]=\"'block'\"\n    >\n    </e11-button>\n  </div>\n  <div class=\"e11-mb-1 e11-px-6 lg:e11-px-16\">\n    <e11-button\n      id=\"button_submit\"\n      value=\"{{ 'Remove photo' | translate }}\"\n      color=\"secondary\"\n      [type]=\"'button'\"\n      [ghost]=\"true\"\n      [icon]=\"'cancel'\"\n      [iconPosition]=\"'left'\"\n      (click)=\"removeCurrentPhoto()\"\n      [disabled]=\"!currentPhotoUrl || loading\"\n      [display]=\"'block'\"\n    >\n    </e11-button>\n  </div>\n</div>\n", styles: [".input-file-uploader{overflow:hidden;opacity:0;height:0;width:0}avatar-upload{position:relative;max-width:6.25rem;margin:.625rem auto 1.875rem}.avatar-upload .avatar-edit{position:absolute;right:19px;z-index:1;top:13px}.avatar-upload .avatar-edit .custom-label{display:inline-block;width:2.125rem;height:2.125rem;margin-bottom:0;border-radius:100%;background:#3efc78;border:1px solid transparent;cursor:pointer;font-weight:400;transition:all .2s ease-in-out}.avatar-upload .avatar-edit .upload-image{background:#3efc78}.avatar-upload .avatar-edit .remove-image{background:#e91e63}.avatar-upload .avatar-edit .custom-label:hover{opacity:1}.avatar-upload .avatar-edit .custom-label:after{position:absolute;text-align:center;margin:auto;top:.5rem;right:0;left:2px;width:1.125rem}.avatar-upload .avatar-preview{width:100px;height:100px;position:relative;border-radius:100%;overflow:hidden;margin:0 auto;margin-bottom:1.5rem;background:#e3e3e3;display:flex;flex-direction:column;justify-content:center;align-items:center}.avatar-upload .avatar-preview>div{width:100%;height:100%;border-radius:100%;background-size:cover;background-repeat:no-repeat;background-position:center}.avatar-upload .avatar-preview>.default{font-size:3.5rem;color:#acacac}\n"] }]
        }], ctorParameters: () => [{ type: FilesService }, { type: i2.E11Logger }], propDecorators: { currentPhotoUrl: [{
                type: Input
            }], currentPhotoReceipt: [{
                type: Input
            }], mode: [{
                type: Input
            }], makePublic: [{
                type: Input
            }], newPhotoUrl: [{
                type: Output
            }], newPhotoReceipt: [{
                type: Output
            }], removePhoto: [{
                type: Output
            }], photoInput: [{
                type: ViewChild,
                args: ['fileInput']
            }] } });

class PhotoUploadComponent {
    constructor(filesService, logger) {
        this.filesService = filesService;
        this.logger = logger;
        this.currentPhotoReceipts = [];
        this.publicFile = true;
        this.photoLimit = Infinity;
        this.newPhotoReceipts = new EventEmitter();
        this.loading = false;
    }
    ngOnInit() { }
    fileInputChange(event) {
        const files = Array.from(event.target.files);
        this.logger.log({ files });
        this.uploadFiles(files);
    }
    dragOverHandler(event) {
        event.preventDefault();
    }
    dropHandler(event) {
        event.preventDefault(); // TODO Check if needed
        const items = Array.from(event.dataTransfer.items);
        const files = items.map((item) => item.getAsFile());
        this.logger.log('files', files);
        this.uploadFiles(files);
    }
    /**
     *
     * @param event
     */
    async uploadFiles(files) {
        this.loading = true;
        const currentFilesGroupingId = this.currentPhotoReceipts?.length ? this.currentPhotoReceipts[0].groupingId : undefined;
        const filesToUpload = files.slice(0, this.photoLimit - this.currentPhotoReceipts.length);
        try {
            const uploadedPhotoReceipts = await this.filesService.uploadFiles({
                files: filesToUpload,
                groupingId: this.folder ?? currentFilesGroupingId,
                makePublic: this.publicFile,
            });
            this.logger.log({ uploadedPhotoUrls: uploadedPhotoReceipts });
            const currentPhotos = this.currentPhotoReceipts;
            const photos = uniq([...currentPhotos, ...uploadedPhotoReceipts]); // prevents duplicate URLs
            this.newPhotoReceipts.emit(photos);
            this.currentPhotoReceipts = photos;
        }
        catch (e) {
            this.logger.error(e); // error handler has already been called
        }
        this.loading = false;
        this.photoInput.nativeElement.value = ''; // Needed so that uploading the same photo after removing it does not bug out
    }
    /**
     *
     * @param photoToRemove
     */
    async removePhoto(photoToRemove) {
        this.loading = true;
        const currentPhotos = this.currentPhotoReceipts;
        const photos = currentPhotos.filter((photo) => photo !== photoToRemove);
        // await this.filesService.delete(photoToRemove.secretToken)
        this.newPhotoReceipts.emit(photos);
        this.currentPhotoReceipts = photos;
        this.loading = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PhotoUploadComponent, deps: [{ token: FilesService }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: PhotoUploadComponent, selector: "sdk-photo-upload", inputs: { folder: "folder", currentPhotoReceipts: "currentPhotoReceipts", publicFile: "publicFile", photoLimit: "photoLimit" }, outputs: { newPhotoReceipts: "newPhotoReceipts" }, viewQueries: [{ propertyName: "photoInput", first: true, predicate: ["photoInput"], descendants: true }], ngImport: i0, template: `
    <div class="e11-h-auto e11-grid md:e11-grid-cols-4">
      <span
        class="e11-bg-skin-primary-100 md:e11-col-span-1 e11-row-span-full e11-flex e11-justify-center e11-items-center e11-flex-col e11-w-full e11-relative"
        (drop)="dropHandler($event)"
        (dragover)="dragOverHandler($event)"
      >
        <div
          class="loader e11-absolute e11-w-full e11-h-full e11-flex e11-justify-center e11-items-center e11-transition-all e11-duration-200 e11-bg-white/80"
          [ngClass]="{ active: loading }"
        >
          <e11-loader-dots [size]="'sm'" [color]="'e11-bg-skin-primary'" label="{{ ' ' | translate }}"></e11-loader-dots>
        </div>
        <div *ngIf="currentPhotoReceipts.length < photoLimit">
          <span class="material-icons-outlined md-48"> file_upload </span>
          <p class="e11-hidden lg:e11-block e11-text-center e11-m-1">{{ 'Drag and drop your images' | translate }}</p>
          <input #photoInput class="e11-hidden" type="file" multiple accept=".png, .jpg, .jpeg" (change)="fileInputChange($event)" />
          <e11-button class="e11-m-2" [value]="'Browse' | translate" (click)="photoInput.click()"></e11-button>
        </div>
      </span>
      <span class="md:e11-col-span-3">
        <div class="e11-w-28 e11-h-28 e11-relative e11-float-left e11-m-4" *ngFor="let photo of currentPhotoReceipts">
          <img class="e11-object-contain e11-h-full e11-w-full" src="{{ photo.url }}" />
          <div
            class="e11-h-5 e11-w-5 e11-flex e11-items-center e11-justify-center e11-cursor-pointer e11-absolute e11--top-2 e11--right-2 e11-text-gray-600 e11-bg-slate-100 e11-rounded-full hover:e11-text-black hover:e11-border-black e11-border e11-border-slate-400 e11-border-solid hover:e11-transition-all e11-ease-in-out"
          >
            <span class="material-icons md-18" (click)="removePhoto(photo)"> close </span>
          </div>
        </div>
      </span>
    </div>
  `, isInline: true, styles: [".loader{z-index:-1;opacity:0}.loader.active{z-index:1;opacity:1}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i5.E11LoaderDotsComponent, selector: "e11-loader-dots", inputs: ["color", "size", "fontSize", "fontColor", "label", "buttonLoader", "fade", "fullScreen", "active", "hasBackdrop", "backdropStyle"] }, { kind: "pipe", type: i6$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PhotoUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-photo-upload', template: `
    <div class="e11-h-auto e11-grid md:e11-grid-cols-4">
      <span
        class="e11-bg-skin-primary-100 md:e11-col-span-1 e11-row-span-full e11-flex e11-justify-center e11-items-center e11-flex-col e11-w-full e11-relative"
        (drop)="dropHandler($event)"
        (dragover)="dragOverHandler($event)"
      >
        <div
          class="loader e11-absolute e11-w-full e11-h-full e11-flex e11-justify-center e11-items-center e11-transition-all e11-duration-200 e11-bg-white/80"
          [ngClass]="{ active: loading }"
        >
          <e11-loader-dots [size]="'sm'" [color]="'e11-bg-skin-primary'" label="{{ ' ' | translate }}"></e11-loader-dots>
        </div>
        <div *ngIf="currentPhotoReceipts.length < photoLimit">
          <span class="material-icons-outlined md-48"> file_upload </span>
          <p class="e11-hidden lg:e11-block e11-text-center e11-m-1">{{ 'Drag and drop your images' | translate }}</p>
          <input #photoInput class="e11-hidden" type="file" multiple accept=".png, .jpg, .jpeg" (change)="fileInputChange($event)" />
          <e11-button class="e11-m-2" [value]="'Browse' | translate" (click)="photoInput.click()"></e11-button>
        </div>
      </span>
      <span class="md:e11-col-span-3">
        <div class="e11-w-28 e11-h-28 e11-relative e11-float-left e11-m-4" *ngFor="let photo of currentPhotoReceipts">
          <img class="e11-object-contain e11-h-full e11-w-full" src="{{ photo.url }}" />
          <div
            class="e11-h-5 e11-w-5 e11-flex e11-items-center e11-justify-center e11-cursor-pointer e11-absolute e11--top-2 e11--right-2 e11-text-gray-600 e11-bg-slate-100 e11-rounded-full hover:e11-text-black hover:e11-border-black e11-border e11-border-slate-400 e11-border-solid hover:e11-transition-all e11-ease-in-out"
          >
            <span class="material-icons md-18" (click)="removePhoto(photo)"> close </span>
          </div>
        </div>
      </span>
    </div>
  `, styles: [".loader{z-index:-1;opacity:0}.loader.active{z-index:1;opacity:1}\n"] }]
        }], ctorParameters: () => [{ type: FilesService }, { type: i2.E11Logger }], propDecorators: { folder: [{
                type: Input
            }], currentPhotoReceipts: [{
                type: Input
            }], publicFile: [{
                type: Input
            }], photoLimit: [{
                type: Input
            }], newPhotoReceipts: [{
                type: Output
            }], photoInput: [{
                type: ViewChild,
                args: ['photoInput']
            }] } });

class FilesComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: FilesComponent, selector: "lib-files", ngImport: i0, template: ` <p>files works!</p> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-files', template: ` <p>files works!</p> ` }]
        }], ctorParameters: () => [] });

const ImportExportModules = [
    E11ButtonModule,
    E11LoaderDotsModule,
    E11DividerModule,
    E11FileViewerModule,
    E11ConfirmationModule,
    E11TooltipModule,
    E11EmptyStateModule,
];
class UiComponentLibraryModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiComponentLibraryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: UiComponentLibraryModule, imports: [CommonModule, E11ButtonModule,
            E11LoaderDotsModule,
            E11DividerModule,
            E11FileViewerModule,
            E11ConfirmationModule,
            E11TooltipModule,
            E11EmptyStateModule], exports: [E11ButtonModule,
            E11LoaderDotsModule,
            E11DividerModule,
            E11FileViewerModule,
            E11ConfirmationModule,
            E11TooltipModule,
            E11EmptyStateModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiComponentLibraryModule, imports: [CommonModule, ImportExportModules, E11ButtonModule,
            E11LoaderDotsModule,
            E11DividerModule,
            E11FileViewerModule,
            E11ConfirmationModule,
            E11TooltipModule,
            E11EmptyStateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiComponentLibraryModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule, ...ImportExportModules],
                    exports: [...ImportExportModules],
                }]
        }] });

const declareExportComponents = [
    PhotoUploadComponent,
    FileUploadComponent,
    FileDropComponent,
    FileListComponent,
    FileLineItemComponent,
    PhotoEditComponent,
];
class FilesModule {
    static forRoot(config) {
        return {
            ngModule: FilesModule,
            providers: [
                {
                    provide: BaseFilesConfigProvider,
                    useClass: config.configProvider,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: FilesModule, declarations: [FilesComponent, PhotoUploadComponent,
            FileUploadComponent,
            FileDropComponent,
            FileListComponent,
            FileLineItemComponent,
            PhotoEditComponent, FileTypeIsImagePipe, FileTypeDisplayPipe], imports: [TranslateModule, CommonModule, UiComponentLibraryModule, LetDirective], exports: [FilesComponent, PhotoUploadComponent,
            FileUploadComponent,
            FileDropComponent,
            FileListComponent,
            FileLineItemComponent,
            PhotoEditComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesModule, imports: [TranslateModule, CommonModule, UiComponentLibraryModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FilesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FilesComponent, ...declareExportComponents, FileTypeIsImagePipe, FileTypeDisplayPipe],
                    imports: [TranslateModule, CommonModule, UiComponentLibraryModule, LetDirective],
                    exports: [FilesComponent, ...declareExportComponents],
                }]
        }] });

/**
 * Direct upload service for admins to directly upload files using custom paths.
 */
class DirectUploadService extends CoreUploadService {
    constructor(configProvider, fileValidationService, errorHandler, storageService) {
        super(configProvider, fileValidationService);
        this.configProvider = configProvider;
        this.fileValidationService = fileValidationService;
        this.errorHandler = errorHandler;
        this.storageService = storageService;
    }
    async uploadFile(request) {
        try {
            return await this.storageUpload(request);
        }
        catch (error) {
            this.errorHandler.handleError(error, { alertUser: true });
            throw error;
        }
    }
    async storageUpload(request) {
        const { file, customPath, uploadSettings, ...restOfUploadRequest } = request;
        const { validation, appUserId, makePublic } = await this.getUploadRequestDefaults(restOfUploadRequest);
        this.validateFileUpload(file, validation);
        const fileName = file.name.replace(/ /g, '_');
        const fileReceipt = {
            appUserId,
            fileName,
            mimeType: file.type,
            size: file.size,
            id: uuid(),
            public: makePublic,
        };
        const result = await this.directUpload(file, fileReceipt, { uploadSettings, customPath });
        if (fileReceipt.public) {
            fileReceipt.url = this.constructFileUrl(result.fullPath);
            await firstValueFrom(this.restClient.put('files/direct-upload/make-public', fileReceipt));
        }
        return fileReceipt;
    }
    async directUpload(file, fileReceipt, options) {
        const { uploadSettings, customPath } = options || {};
        return await this.storageService.uploadWithMetadata(file, this.buildPath(fileReceipt, { customPath }), fileReceipt, uploadSettings);
    }
    async getUploadRequestDefaults(request) {
        const { makePublic = false, validation: customValidation, appUserId, ...rest } = request;
        const requestWithoutUndefined = stripUndefined(rest);
        const validation = { ...this.validation, ...customValidation };
        return { appUserId, makePublic, ...requestWithoutUndefined, validation };
    }
    buildPath(model, options) {
        const { fileName, public: isPublic = false, customPath } = { ...model, ...options };
        const storageRoot = isPublic ? CONSTANTS.PUBLIC_STORAGE_ROOT : CONSTANTS.STORAGE_ROOT;
        return `${storageRoot}${this.sanitizedPathname(customPath)}/${fileName}`;
    }
    sanitizedPathname(path) {
        return (path
            // Remove repeated slashes, trailing slash, and leading slash
            .replace(/\/+/g, '/')
            .replace(/\/$/, '')
            .replace(/^\//, ''));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: DirectUploadService, deps: [{ token: BaseFilesConfigProvider }, { token: FileValidationService }, { token: i2.E11ErrorHandlerService }, { token: i1$1.StorageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: DirectUploadService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: DirectUploadService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: BaseFilesConfigProvider }, { type: FileValidationService }, { type: i2.E11ErrorHandlerService }, { type: i1$1.StorageService }] });

const availablePlatforms = Object.freeze({
    mux: 'mux_video',
    cloudflare: 'cloudflare_stream',
});

const COMPOSITE_KEY_SEPARATOR = '::';
function parseCompositeKey(key) {
    const parts = key.split(COMPOSITE_KEY_SEPARATOR);
    const [platform] = parts;
    const values = parts.slice(1).reduce((acc, part) => {
        const [k, v] = part.split('=');
        if (k && v)
            acc[k] = v;
        return acc;
    }, {});
    values['platform'] = platform;
    return values;
}

class CloudflareProvider {
    canHandle(key) {
        return key.startsWith(availablePlatforms.cloudflare);
    }
    buildUrl(file) {
        const parsed = parseCompositeKey(file.videoCompositeKey);
        const { videoId } = parsed;
        if (!videoId)
            throw new Error('Missing videoId in videoCompositeKey');
        return `https://videodelivery.net/${videoId}/manifest/video.m3u8`;
    }
}

class GoogleProvider {
    canHandle() {
        return true; // Always fallback
    }
    buildUrl(file) {
        return file.signedURL ?? file.url ?? '';
    }
}

class MuxProvider {
    canHandle(key) {
        return key.startsWith(availablePlatforms.mux);
    }
    buildUrl(file) {
        const parsed = parseCompositeKey(file.videoCompositeKey);
        const { playbackId } = parsed;
        if (!playbackId)
            throw new Error('Missing playbackId in videoCompositeKey');
        return `https://stream.mux.com/${playbackId}.m3u8`;
    }
}

class VideoProviderFactory {
    constructor() {
        this.providers = [];
        this.providers = [new MuxProvider(), new CloudflareProvider(), new GoogleProvider()];
    }
    getProvider(key) {
        return this.providers.find((p) => p.canHandle(key ?? ''));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: VideoProviderFactory, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: VideoProviderFactory, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: VideoProviderFactory, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

class VideoUrlBuilder {
    constructor(factory) {
        this.factory = factory;
    }
    build(file) {
        const key = file.videoCompositeKey ?? '';
        const provider = this.factory.getProvider(key);
        return provider.buildUrl(file);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: VideoUrlBuilder, deps: [{ token: VideoProviderFactory }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: VideoUrlBuilder, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: VideoUrlBuilder, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: VideoProviderFactory }] });

/*
 * Public API Surface of files
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BaseFilesConfigProvider, COLLECTIONS, CONSTANTS, DEFAULT_FILE_VALIDATION, DirectUploadService, FILES_ERRORS, FILE_MODE, FileDropComponent, FileLineItemComponent, FileListComponent, FileUploadComponent, FileUploadDefaultFileTypes, FileValidationService, FilesComponent, FilesConfigProvider, FilesModule, FilesService, PhotoEditComponent, PhotoUploadComponent, UploadFileStore, VideoProviderFactory, VideoUrlBuilder, availablePlatforms, fileAdapter, parseCompositeKey };
